import Bugsnag from '@bugsnag/js';
import Environment from '@eatzy/common-reactjs/Environment';
import { isTabletPairingUrl } from '../utils/tabletPairingPrivacy';

// Whitelist of safe fields to forward from the API response body to Bugsnag.
// We intentionally do NOT forward `responseBody` itself: backend responses for
// /restmanager endpoints can contain restaurant/customer PII (names, emails,
// addresses, order details). The fields below are diagnostic only.
const sanitizeResponseBody = (body) => {
  if (!body || typeof body !== 'object') return undefined;
  const safe = {};
  if (typeof body.message === 'string') {
    safe.message = body.message.slice(0, 500);
  }
  if (typeof body.code === 'string' || typeof body.code === 'number') {
    safe.code = body.code;
  }
  // Schema-validation errors include the offending property path (e.g.
  // "date_field"), which is what diagnosed today's 400. The path is a
  // developer-facing field name, not user data.
  if (Array.isArray(body.errors)) {
    safe.validationErrors = body.errors.slice(0, 5).map((e) => ({
      message: typeof e?.message === 'string' ? e.message.slice(0, 300) : undefined,
      path: typeof e?.path === 'string' ? e.path : undefined,
    }));
  }
  return Object.keys(safe).length ? safe : undefined;
};

// Pull diagnostic-only fields out of the URL: pathname, restaurant id (when
// embedded as `/restaurants/{id}/...`), and the date window (start/end query
// params). All three live in the URL the SPA built itself, so this is safe.
const parseUrlContext = (url) => {
  if (!url) return {};
  const out = {};
  let pathname;
  let searchParams;
  try {
    if (typeof url === 'string') {
      const parsed = new URL(url);
      pathname = parsed.pathname;
      searchParams = parsed.searchParams;
    } else if (url.pathname !== undefined) {
      pathname = url.pathname;
      searchParams = url.searchParams;
    }
  } catch (_) {
    return out;
  }
  if (pathname) {
    out.endpoint = pathname;
    const m = pathname.match(/\/restaurants\/([^/]+)/);
    if (m) out.restaurantId = m[1];
  }
  if (searchParams) {
    const start = searchParams.get('start');
    const end = searchParams.get('end');
    if (start || end) {
      out.window = {
        start: start || undefined,
        end: end || undefined,
      };
    }
  }
  return out;
};

// RFC4122 v4 UUID generator. Prefers the platform `crypto.randomUUID()` so
// production traffic gets a CSPRNG-backed id; falls back to Math.random in
// jsdom and ancient browsers that lack the API. The id is only ever used as
// a correlation key for support lookups, so the fallback's weaker entropy
// is not a security concern.
const generateRequestId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    try {
      return crypto.randomUUID();
    } catch (_) {
      // Fall through to the Math.random path below.
    }
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Pulls `X-Request-Id` out of a headers map. Accepts the shape attached to
// the rejected payload by the response interceptor (`{ 'x-request-id': ... }`)
// as well as a real `Headers` instance, so callers don't have to normalize.
const readHeaderRequestId = (headers) => {
  if (!headers) return undefined;
  if (typeof headers.get === 'function') {
    return headers.get('X-Request-Id') || headers.get('x-request-id') || undefined;
  }
  if (typeof headers === 'object') {
    return (
      headers['x-request-id'] ||
      headers['X-Request-Id'] ||
      headers['X-Request-ID'] ||
      undefined
    );
  }
  return undefined;
};

// Wraps a fetch failure into a real Error that callers can pass straight
// to Bugsnag, while preserving backward-compatible `.message` and `.status`
// for any existing code that reads them. The original error body is NOT
// kept on the error — only a sanitized summary on `__apiContext`.
//
// Request-id resolution priority:
//   1. `X-Request-Id` response header (authoritative — set by the API gateway)
//   2. body fields (`request_id`, `requestId`, `id`)
//   3. the SPA-generated id we sent in the request `X-Request-Id` header
// (3) is the safety net so the user always has a Ref to give support, even
// for network errors and gateway failures that never produced a header.
const enrichApiError = ({ method, url, err, responseHeaders, sentRequestId }) => {
  const urlString = url?.toString?.() || String(url || '');
  const status = err?.status;
  const baseMessage =
    err?.message ||
    err?.errors?.[0]?.message ||
    `HTTP ${status || 'error'} ${method || 'GET'} ${urlString}`.trim();

  const wrapped = err instanceof Error ? err : new Error(baseMessage);
  if (!(err instanceof Error) && err && typeof err === 'object') {
    // Preserve enumerable fields callers may still read (e.g. .status, .message).
    Object.assign(wrapped, err);
  }

  const headerRequestId =
    readHeaderRequestId(responseHeaders) ||
    readHeaderRequestId(err?.responseHeaders);

  const bodyRequestId =
    err?.request_id ||
    err?.requestId ||
    err?.id ||
    undefined;

  const requestId = headerRequestId || bodyRequestId || sentRequestId || undefined;
  const requestIdSource = headerRequestId
    ? 'header'
    : bodyRequestId
    ? 'body'
    : sentRequestId
    ? 'sent'
    : undefined;

  // Always overwrite the wrapped error's requestId so the most authoritative
  // source wins (e.g. when Object.assign copied a body-level requestId but we
  // also got a header — header should take precedence).
  if (requestId) wrapped.requestId = requestId;

  wrapped.__apiContext = {
    method: method || 'GET',
    url: urlString,
    status: wrapped.status,
    requestId,
    requestIdSource,
    sentRequestId,
    ...parseUrlContext(url),
    response: sanitizeResponseBody(err),
  };

  return wrapped;
};

// Centralized API failure reporter. Fires for EVERY non-2xx and every
// network error from the API client, so even unmodified call sites
// (or sites that forget to use notifyError) still surface API failures
// to Bugsnag with full HTTP context. UI catch sites that call
// notifyError() will produce a second event carrying the call-site
// component/method — Bugsnag groups them together by stack hash.
const reportApiFailure = (wrappedErr) => {
  try {
    Bugsnag.notify(wrappedErr, (event) => {
      if (wrappedErr && wrappedErr.__apiContext) {
        event.addMetadata('api', wrappedErr.__apiContext);
      }
    });
  } catch (_) {
    // Never let instrumentation throw from the request path.
  }
};

export default class API {
  buildConfig = async ({ endpoint, body, params, method, customConfig }) => {
    const baseUrl = Environment.getEnv('API_URL');

    const url = new URL(baseUrl + endpoint);
    Object.keys(params || {}).forEach((key) => {
      url.searchParams.append(key, params?.[key]);
    });

    const config = {
      method,
      ...customConfig,
    };

    if (customConfig?.isMultiPart && body) {
      config.body = body;
    } else if (body) {
      config.body = JSON.stringify(body);
    }

    return [url, config];
  };

  improvedFetch = async (
    endpoint,
    { body, params, method, noAutoRetry, ...customConfig },
    attemptsLeft,
    retryDelay = 0
  ) => {
    const [url, config] = await this.buildConfig({
      endpoint,
      body,
      params,
      method,
      customConfig,
    });
    const pairingRequest = isTabletPairingUrl(url);

    // Attach an SPA-generated `X-Request-Id` to every outbound request. The
    // gateway echoes it back in the response header, but we also keep it in
    // closure so a network error (no response, no header) still surfaces a
    // ref id the user can hand to support. Generated per-attempt: a 403
    // refresh-and-retry below is a NEW request, so it gets a NEW id.
    const sentRequestId = generateRequestId();
    config.headers = {
      ...(config.headers || {}),
      'X-Request-Id': sentRequestId,
    };

    return window.fetch(url, config).catch(async (err) => {
      // Caller-driven cancellation (e.g. a newer request superseded this
      // one via AbortController). NOT a failure: don't refresh-and-retry,
      // don't enrich, and don't notify Bugsnag — just propagate the
      // AbortError so the caller can ignore it.
      if (err?.name === 'AbortError' || config.signal?.aborted) {
        return Promise.reject(err);
      }
      // The 403 refresh-and-retry path silently re-issues the SAME logical
      // request. Callers that need deterministic single-attempt traffic
      // (e.g. stripe-recon, where backend asked us not to retry) opt out
      // via { noAutoRetry: true } and the failure surfaces immediately.
      if (!pairingRequest && !noAutoRetry && attemptsLeft > 0 && err?.status === 403) {
        if (retryDelay > 0) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
        }
        return await this.improvedFetch(
          endpoint,
          { body, params, method, noAutoRetry, ...customConfig },
          attemptsLeft - 1,
          retryDelay
        );
      }
      // Pairing's service projects only the shared safe error envelope. Never
      // attach a raw response, request body, key, or fetch error to telemetry.
      if (pairingRequest) return Promise.reject(err);
      const enriched = enrichApiError({
        method,
        url,
        err,
        responseHeaders: err?.responseHeaders,
        sentRequestId,
      });
      reportApiFailure(enriched);
      return Promise.reject(enriched);
    });
  };

  get = async (endpoint, params, options = {}) =>
    this.improvedFetch(endpoint, { method: 'GET', params, ...options }, 1);

  delete = async (endpoint, body, params, options = {}) =>
    this.improvedFetch(
      endpoint,
      { method: 'DELETE', body, params, ...options },
      1
    );

  post = async (endpoint, body, options = {}) =>
    this.improvedFetch(endpoint, { method: 'POST', body, ...options }, 1);

  put = async (endpoint, body, options = {}) =>
    this.improvedFetch(endpoint, { method: 'PUT', body, ...options }, 1);

  patch = async (endpoint, body, options = {}) =>
    this.improvedFetch(endpoint, { method: 'PATCH', body, ...options }, 1);

  upload = async (endpoint, body, options = {}) =>
    this.improvedFetch(
      endpoint,
      {
        method: 'POST',
        body,
        isMultiPart: true,
        ...options,
      },
      1
    );
}

export const api = new API();
